import * as React from 'react';
import { TableCell } from 'types/common/table';
import { ConnectCommunityUserStatus, ConnectMember, ConnectUserStatus } from 'types/member';
import { getUtcUSFormatDateWithTime } from 'modules/utils/time';
import { DEFAULT_PAGE_SIZE } from 'constants/common';
import { getTableRowNumber } from 'modules/dataTable';
import { connectCommunityUserTypeLabel, connectUserTypeLabel } from 'constants/member/connect';
import { sellerStatusLabel } from 'constants/marketplace/confirmSellerInfo';
import { MemberNameLink, TextWrapper } from 'components/member/common';

const EMPTY_CELL = '-';

const getAbusingUserLabels = (rowData: ConnectMember): string[] => {
  const abusingLabels = [];
  if (rowData.connectUserStatus === ConnectUserStatus.INACTIVE) abusingLabels.push('Payment');
  if (rowData.connectCommunityUserStatus === ConnectCommunityUserStatus.INACTIVE)
    abusingLabels.push('Community');
  return abusingLabels;
};

export const connectMemberListColumns = (
  totalItems: number,
  currentPageIndex: number,
): TableCell<ConnectMember>[] => [
  {
    align: 'center',
    name: 'No.',
    key: 'no',
    render: (rowData, columnData, rowIndex) =>
      getTableRowNumber(totalItems, DEFAULT_PAGE_SIZE, currentPageIndex, rowIndex),
  },
  {
    align: 'center',
    name: 'Email',
    key: 'email',
    render: (rowData: ConnectMember) => (
      <MemberNameLink to={`/member/${rowData.userid}/general`}>
        <TextWrapper>{rowData.email}</TextWrapper>
      </MemberNameLink>
    ),
  },
  {
    align: 'center',
    name: 'User Type',
    key: 'userType',
    // 1920 해상도 기준 'CLO Creator Collective' 가 2줄로 노출되는 폭
    width: '140px',
    render: (rowData: ConnectMember) =>
      connectUserTypeLabel[rowData.connectUserType] ?? EMPTY_CELL,
  },
  {
    align: 'center',
    name: 'Community User Type',
    key: 'communityUserType',
    width: '130px',
    render: (rowData: ConnectMember) =>
      connectCommunityUserTypeLabel[rowData.connectCommunityUserType] ?? EMPTY_CELL,
  },
  {
    align: 'center',
    name: 'Abusing User',
    key: 'abusingUser',
    width: '110px',
    render: (rowData: ConnectMember) => {
      // 둘 다 Active 면 '-', Inactive 인 항목명 표시 (둘 다면 병기)
      const abusingLabels = getAbusingUserLabels(rowData);
      if (!abusingLabels.length) return EMPTY_CELL;
      return (
        <>
          {abusingLabels.map(label => (
            <div key={label}>{label}</div>
          ))}
        </>
      );
    },
  },
  {
    align: 'center',
    name: 'Upload Item',
    key: 'uploadItem',
    width: '110px',
    render: (rowData: ConnectMember) => rowData.uploadItemCount || EMPTY_CELL, // 0 이면 '-'
  },
  {
    align: 'center',
    name: 'Seller Info',
    key: 'sellerInfoStatus',
    width: '110px',
    render: (rowData: ConnectMember) =>
      // Confirm Seller Info 와 동일한 상태 값 표시. 제출 이력 없으면(null/0) '-'
      (rowData.sellerInfoStatus && sellerStatusLabel[rowData.sellerInfoStatus]) || EMPTY_CELL,
  },
  {
    align: 'center',
    name: 'Marketing Agreement',
    key: 'marketingAgreement',
    width: '170px',
    render: (rowData: ConnectMember) => {
      return <>{rowData.marketingAgree ? 'Yes' : 'No'}</>;
    },
  },
  {
    align: 'center',
    name: 'Registered Date',
    key: 'registeredDate',
    width: '220px',
    render: (rowData: ConnectMember) => getUtcUSFormatDateWithTime(rowData.registrationDate, true),
  },
];
